import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { compose } from 'redux';
import { hot } from 'react-hot-loader/root';
import classNames from 'classnames';
import {get, some, pick} from 'lodash';
import Header from '../header';
import Footer from '../footer';
import AdhesionAd from '../ad/adhesion_ad';
import OutOfPageAd from '../ad/out_of_page_ad';
import Ad from '../common/ad';
import Spinner from '../common/spinner';
import MediaQuery from '../common/media_query';
import withScrollDepthTracking from '../hocs/withScrollDepthTracking';
import commonPropTypes from '../../containers/commonPropTypes';
import { isHomepageRefresh } from '../../utils/homepage_refresh/homepage';
import { onHomepage } from '../../utils/dom';
import { ARTICLE_CUSTOM_PAGE_TYPE, CHANNEL_PAGE_TYPE } from '../../constants';
import { BeautyconLogo, BeautyconMenuLogo, SomosLogo, SomosMenuLogo, UnbotheredLogo, UnbotheredMenuLogo } from '../header/vertical_logos';

/*
 * The Secondary Wrapper is the div that wraps the Header, Footer
 * and actual content in the `r29-container`.
 *
 * TODO: It would be terrific to also create a PrimaryWrapper component and extract
 * some more of the presentational logic from `createRosettaContainer` into
 * `js/components/root/primary_wrapper`.
 */

export const SecondaryWrapper = (props) => {
  const {
    shouldDisplayInvertedStyles,
    shouldDisplayOutOfPageAd,
    shouldDisplayAdhesionAd,
    shouldDisplayHeaderAd,
    shouldDisplayCondensedModules,
    contentAboveModules,
    modules,
    contentBelowModules
  } = props.containerMethods;

  const {
    adsState,
    alternate_urls,
    analytics,
    api,
    location,
    edition_data,
    footerLinks,
    geolocation,
    meta_data,
    modules: modulesArray,
    navigationItems,
    newsletters,
    recordSearchTerm,
    setShouldShowGeolocationBanner,
    shouldShowGeolocationBanner,
    trendingItems
  } = props;

  const secondaryWrapperClassName = classNames('main', {
    inverted: shouldDisplayInvertedStyles(),
    'shopping-home': get(props, 'productList.isShoppingHome'),
    'condensed-modules': shouldDisplayCondensedModules()
  });

  const outOfPageAd = shouldDisplayOutOfPageAd() && (
    <OutOfPageAd adsState={adsState} />
  );

  const adhesionAd = shouldDisplayAdhesionAd() && (
    <AdhesionAd adsState={adsState} />
  );

  const isCustomArticle = analytics.page_type === ARTICLE_CUSTOM_PAGE_TYPE;

  const r29Content = (
    <div id="r29-container">
      {
        /*
         * The Global Spinner only displays if we're changing pathnames.
         * When we're on the same pathname but still changing route,
         * (fetchStatus === 'fetchingOnSamePage'), or when we're clicking on
         * a More Stories button (fetchStatus === 'moreContent'), we
         * rely on inline spinners.
         */
        api.fetchStatus === 'fetchingNewPage' && <Spinner />
      }
      {onHomepage(location, edition_data) && (
        <Ad
          adsState={adsState}
          sizes={[[1, 2]]}
          uniqueId={'native-promo'}
          position={'promo-homepage'}
          showAdCallout={false}
        />
      )}
      {contentAboveModules()}
      {!isCustomArticle && modules()}

      {contentBelowModules()}
      {adhesionAd}
    </div>
  );

  const footerProps = {
    links: footerLinks,
    geolocation: geolocation,
    edition_data: edition_data,
    newsletters: newsletters,
    meta_data: meta_data,
    modules: modulesArray,
    pageId: analytics.type_id,
  };

  const secondaryWrapperContents = (
    <Fragment>
      <Header
        adsState={adsState}
        alternate_urls={alternate_urls}
        navigationItems={navigationItems}
        trendingItems={trendingItems}
        page_type={analytics.page_type}
        pageId={analytics.type_id}
        recordSearchTerm={recordSearchTerm}
        shouldDisplayAd={shouldDisplayHeaderAd()}
        hasHeroModule={some(modulesArray, { type: 'hero' })}
        isShoppingHome={get(props, 'productList.isShoppingHome', false)}
        edition_data={edition_data}
        geolocation={geolocation}
        shouldShowGeolocationBanner={shouldShowGeolocationBanner}
        setShouldShowGeolocationBanner={setShouldShowGeolocationBanner}
      />
      <div className="mobile-header-verticals-wrapper">
        <div className="mobile-header-verticals">
          <div className="mobile-vertical">
            <a href="https://www.refinery29.com/en-us/unbothered">{UnbotheredMenuLogo}</a>
          </div>
          <div className="mobile-vertical">
            <a href="https://www.refinery29.com/en-us/latine-somos">{SomosMenuLogo}</a>
          </div>
          <div className="mobile-vertical">
            <a href="https://www.beautycon.com/">{BeautyconMenuLogo}</a>
          </div>
        </div>
      </div>
      {r29Content}
      <MediaQuery breakpoint="mobile">
        <Footer
          {...footerProps}
        />
      </MediaQuery>
      <MediaQuery breakpoint="notMobile">
        <Footer {...footerProps} />
      </MediaQuery>
      <div className="oop-ad-container">
        {outOfPageAd}
      </div>
      <div id="fb-root" />
    </Fragment>
  );

  return (
    <Fragment>
      <MediaQuery breakpoint="mobile">
        <div
          className={classNames(secondaryWrapperClassName, {
            'homepage-refresh': isHomepageRefresh(location, edition_data, true)
          })}
        >
          {secondaryWrapperContents}
        </div>
      </MediaQuery>
      <MediaQuery breakpoint="notMobile">
        <div className={secondaryWrapperClassName}>
          {secondaryWrapperContents}
        </div>
      </MediaQuery>
    </Fragment>
  );
};

const decorator = compose(
  withScrollDepthTracking({
    shouldTrackScrollEvent: function() {
      return (
        this.props.analytics.page_type === CHANNEL_PAGE_TYPE &&
        this.props.location.pathname !== '/' + this.props.edition_data.basename
      );
    }
  }),
  /*
   * Note: the fact that we're wrapping _this_ component with
   * `react-hot-loader` means that any changes we make above it in the
   * component tree won't have hot loading (i.e., changes will cause a
   * full page refresh).
   *
   * Notably, this includes the <head>, Dash Pass, and a few other things.
   *
   * * We would ideally like to have `hot()` wrap the most top-level component
   *   possible.
   * * It doesn't play nice if you wrap `AppContainer` in `createRosettaContainer.js`.
   * * Recommendation: we can create a <Root> component that has _everything_
   *   in it, including this <SecondaryWrapper>. `createRosettaContainer.js`
   *   would render the <Root>, and the former's code would only be in charge
   *   of connecting to Redux, like a "container" should.
   */
  hot
);

SecondaryWrapper.propTypes = {
  ...pick(commonPropTypes, [
    'adsState',
    'alternate_urls',
    'analytics',
    'api',
    'footerLinks',
    'meta_data',
    'location',
    'modules',
    'navigationItems',
    'newsletters',
    'productList',
    'recordSearchTerm',
    'setShouldShowGeolocationBanner',
    'edition_data',
    'geolocation',
    'shouldShowGeolocationBanner',
    'trendingItems',
    'exeriments'
  ]),
  containerMethods: PropTypes.shape({
    shouldDisplayInvertedStyles: PropTypes.func.isRequired,
    shouldDisplayOutOfPageAd: PropTypes.func.isRequired,
    shouldDisplayAdhesionAd: PropTypes.func.isRequired,
    shouldDisplayHeaderAd: PropTypes.func.isRequired,
    contentAboveModules: PropTypes.func.isRequired,
    modules: PropTypes.func.isRequired,
    contentBelowModules: PropTypes.func.isRequired
  }).isRequired
};

export default decorator(SecondaryWrapper);
